import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { API } from "../../lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm GIA, the Greenville Integrated Solutions assistant. Ask me about access control, AV, cameras, or cabling — or click 'Request Quote' anytime.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm having trouble responding right now. Please try again in a moment, or fill out our Quote form for a fast reply.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle-btn"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 grid place-items-center bg-[#1E3A6B] hover:bg-[#172F58] text-white shadow-lg shadow-[#1E3A6B]/20 transition-all rounded-full"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm bg-white border border-slate-200 shadow-2xl flex flex-col"
          data-testid="chatbot-panel"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
            <img
              src="/logo.png"
              alt="Greenville"
              className="h-11 w-auto object-contain"
            />
            <div>
              <div className="font-display font-bold text-sm text-slate-900">GIA</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500">
                Greenville · AI Assistant
              </div>
            </div>
          </div>
          <div className="flex-1 max-h-96 overflow-y-auto p-4 space-y-3 text-sm" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1E3A6B] text-white"
                      : "bg-slate-100 text-slate-800 border border-slate-200"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 text-slate-500 px-3 py-2 border border-slate-200 inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-2 flex gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our services…"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1E3A6B]"
              disabled={loading}
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={loading || !input.trim()}
              className="px-3 bg-[#1E3A6B] hover:bg-[#172F58] disabled:bg-slate-300 text-white"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
